import React, { useEffect, useRef, useState } from "react";
import { Upload, X, ImageIcon, Video, Info } from "@/components/icons";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DeleteConfirm } from "@/components/DeleteConfirm";

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const TARGET_IMAGE_BYTES = 2 * 1024 * 1024; // ~2 MB

// Compress an image file to roughly TARGET_IMAGE_BYTES using a canvas.
async function compressImage(file) {
  if (!IMAGE_TYPES.includes(file.type)) return file;
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  const maxDim = 1600;
  let { width, height } = bitmap;
  if (width > maxDim || height > maxDim) {
    const scale = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
  let quality = 0.85;
  let blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
  while (blob && blob.size > TARGET_IMAGE_BYTES && quality > 0.4) {
    quality -= 0.15;
    blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
  }
  return new File([blob], (file.name || "foto").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
}

/**
 * MediaUpload — backend-proxied uploads (multipart POST to our API, which
 * stores the file in object storage and returns a media reference).
 * Degrades honestly: when storage is not configured the control stays visible
 * but explains (in Dutch) that media storage is not set up; the parent form can
 * still be submitted without media.
 */
export const MediaUpload = ({ value = [], onChange, apiClient, uploadPath = "/storage/upload", disabled, onStatusChange, confirmRemove = false }) => {
  const [configured, setConfigured] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState([]); // [{id, file, name}]
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    apiClient
      .get("/storage/status")
      .then(({ data }) => alive && setConfigured(!!data.configured))
      .catch(() => alive && setConfigured(false));
    return () => { alive = false; };
  }, [apiClient]);

  // Let the parent form know whether it is safe to submit yet.
  useEffect(() => {
    onStatusChange?.({ uploading: busy, failedCount: failed.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, failed.length]);

  const prepare = (raw) => {
    const isImage = IMAGE_TYPES.includes(raw.type);
    const isVideo = VIDEO_TYPES.includes(raw.type);
    if (!isImage && !isVideo) { toast.error("Alleen foto's (JPG/PNG/WebP) of video (MP4) zijn toegestaan."); return null; }
    if (isVideo && raw.size > 25 * 1024 * 1024) { toast.error("Video is te groot (max 25 MB)."); return null; }
    return { isImage, isVideo };
  };

  // Upload one file; returns the media ref or throws. Keeps successes isolated
  // so one failing upload never discards the ones that already went up.
  const uploadOne = async (raw, kinds) => {
    const file = kinds.isImage ? await compressImage(raw) : raw;
    const form = new FormData();
    form.append("file", file, file.name || (kinds.isVideo ? "video" : "foto"));
    const { data: ref } = await apiClient.post(uploadPath, form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 0 });
    return { key: ref.key, content_type: ref.content_type, kind: ref.kind, size: ref.size };
  };

  const reportError = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 503) toast.error("Media-opslag is nog niet geconfigureerd.");
    else if (status === 400 && typeof detail === "string") toast.error(detail);
    else toast.error("Eén upload mislukte. De andere foto's zijn wel bewaard — probeer 'm opnieuw.");
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let current = [...value];
    for (const raw of Array.from(files)) {
      const kinds = prepare(raw);
      if (!kinds) continue;
      try {
        const ref = await uploadOne(raw, kinds);
        current = [...current, ref];
        onChange(current); // commit each success immediately
      } catch (err) {
        reportError(err);
        setFailed((f) => [...f, { id: `${Date.now()}-${Math.random()}`, file: raw, name: raw.name || "bestand" }]);
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const retry = async (entry) => {
    const kinds = prepare(entry.file);
    if (!kinds) return;
    setBusy(true);
    try {
      const ref = await uploadOne(entry.file, kinds);
      onChange([...value, ref]);
      setFailed((f) => f.filter((x) => x.id !== entry.id));
    } catch (err) {
      reportError(err);
    } finally { setBusy(false); }
  };

  const remove = (key) => onChange(value.filter((m) => m.key !== key));

  if (configured === false) {
    return (
      <div
        data-testid="media-upload-not-configured"
        className="flex items-start gap-2 rounded-md border border-dashed border-border bg-muted/30 p-3 text-body text-muted-foreground"
      >
        <Info size={18} className="mt-0.5 shrink-0" />
        <span>Media-opslag is nog niet geconfigureerd; je kunt wel zonder foto of video melden.</span>
      </div>
    );
  }

  return (
    <div data-testid="media-upload">
      <input
        ref={inputRef}
        type="file"
        accept={[...IMAGE_TYPES, ...VIDEO_TYPES].join(",")}
        multiple
        className="hidden"
        data-testid="media-upload-input"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={disabled || busy || configured === null}
        data-testid="media-upload-button"
        onClick={() => inputRef.current?.click()}
      >
        <Upload size={18} className="mr-2" /> {busy ? "Uploaden..." : "Foto / video toevoegen"}
      </Button>
      {value.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {value.map((m) => (
            <div key={m.key} className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-2 py-1 text-data">
              {m.kind === "video" ? <Video size={14} /> : <ImageIcon size={14} />}
              <span className="max-w-[140px] truncate">{m.kind === "video" ? "Video" : "Foto"}</span>
              {confirmRemove ? (
                <DeleteConfirm
                  trigger={
                    <button type="button" data-testid={`media-remove-${m.key}`} className="text-muted-foreground hover:text-destructive">
                      <X size={14} />
                    </button>
                  }
                  title="Foto verwijderen?"
                  description="Weet je zeker dat je deze foto wilt verwijderen? Dit kan niet ongedaan gemaakt worden."
                  onConfirm={() => remove(m.key)}
                  testid={`media-remove-confirm-${m.key}`}
                />
              ) : (
                <button type="button" onClick={() => remove(m.key)} className="text-muted-foreground hover:text-destructive">
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {failed.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="media-upload-failed">
          {failed.map((entry) => (
            <div key={entry.id} className="flex items-center justify-between gap-2 rounded-md border border-letop-rand bg-letop-vlak px-2 py-1 text-data text-letop-tekst">
              <span className="max-w-[160px] truncate">{entry.name} — niet geüpload</span>
              <Button type="button" size="sm" variant="outline" disabled={busy} data-testid="media-retry" onClick={() => retry(entry)}>Opnieuw</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
